import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Pagination, Stack, Tab, Table,
  TableBody, TableCell, TableHead, TableRow, Tabs, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import api from '../../services/api';

// "Resincronizar todo" con revisión (pedido de Eric, 03/11/2026): primero se recorre el canal sin grabar nada; cada
// diferencia con lo que ya existe (fecha de la venta, estado, total, cliente) y cada venta nueva se aprueba o rechaza
// una por una, y solo se aplica lo aprobado.

export interface ResyncDiff { field: string; label: string; current?: string | null; incoming?: string | null }
export interface ResyncItem {
  externalId: string; kind: 'diff' | 'new'; orderId?: number | null; reference?: string | null; saleDate?: string | null;
  total?: number | null; customer?: string | null; diffs: ResyncDiff[];
}
export interface ResyncPreview {
  id: number; channelId: number; status: 'Revisando' | 'Lista' | 'Error' | 'Aplicando' | 'Aplicada'; scanned: number;
  diffCount: number; newCount: number; error?: string | null; createdAt: string; finishedAt?: string | null; appliedAt?: string | null;
  acceptedCount: number; applyNote?: string | null; items?: ResyncItem[] | null;
}

const PAGE = 50;
const fmtWhen = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '') : '—';
const fmtMoney = (n?: number | null) => (n == null ? '—' : `$${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(n)}`);
const fmtValue = (d: ResyncDiff, v?: string | null) => (d.field === 'date' ? fmtWhen(v) : d.field === 'total' ? fmtMoney(v == null ? null : Number(v)) : v || '—');

const ResyncDialog: React.FC<{ channel: { id: number; name: string }; onClose: () => void; onApplied: () => void }> = ({ channel, onClose, onApplied }) => {
  const [preview, setPreview] = useState<ResyncPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'diff' | 'new'>('diff');
  const [page, setPage] = useState(1);
  const [decisions, setDecisions] = useState<Record<string, boolean>>({});
  const [applying, setApplying] = useState(false);

  const start = useCallback(async () => {
    setError(null);
    setDecisions({});
    try { setPreview(await api.startResync(channel.id)); } catch (e: any) { setError(e?.response?.data?.message || 'No se pudo empezar la revisión'); }
  }, [channel.id]);
  useEffect(() => { start(); }, [start]);

  // Mientras revisa o aplica, se consulta cada 3 segundos.
  useEffect(() => {
    if (!preview || (preview.status !== 'Revisando' && preview.status !== 'Aplicando')) return;
    const t = setTimeout(async () => {
      try {
        const p = await api.getResync(channel.id, preview.id);
        setPreview(p);
        if (p.status === 'Aplicada') onApplied();
      } catch { /* reintenta en la próxima vuelta */ }
    }, 3000);
    return () => clearTimeout(t);
  }, [preview, channel.id, onApplied]);

  const items = useMemo(() => preview?.items ?? [], [preview]);
  const list = useMemo(() => items.filter(i => i.kind === tab), [items, tab]);
  const pageItems = list.slice((page - 1) * PAGE, page * PAGE);
  const decided = items.filter(i => decisions[i.externalId] !== undefined).length;
  const accepted = items.filter(i => decisions[i.externalId] === true);
  const setAll = (value: boolean) => setDecisions(d => { const n = { ...d }; list.forEach(i => { n[i.externalId] = value; }); return n; });

  const apply = async () => {
    if (!preview) return;
    if (!window.confirm(`Se van a aplicar ${accepted.length} de ${items.length} cambios. Lo que marcaste "No" no se toca. ¿Aplicar?`)) return;
    setApplying(true);
    try { setPreview(await api.applyResync(channel.id, preview.id, accepted.map(i => i.externalId))); }
    catch (e: any) { setError(e?.response?.data?.message || 'No se pudo aplicar'); }
    finally { setApplying(false); }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Resincronizar todo · {channel.name}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!preview && !error && <CircularProgress />}

        {preview?.status === 'Revisando' && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
            <Box>
              <Typography fontWeight={600}>Comparando el canal con lo que ya está en el sistema…</Typography>
              <Typography variant="body2" color="text.secondary">{preview.scanned} ventas revisadas. No se graba nada hasta que apruebes.</Typography>
            </Box>
          </Stack>
        )}

        {preview?.status === 'Error' && (
          <Alert severity="error" action={<Button size="small" onClick={start}>Reintentar</Button>}>{preview.error || 'La revisión falló'}</Alert>
        )}

        {preview?.status === 'Aplicando' && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
            <Typography>Aplicando {preview.acceptedCount} cambios aprobados…</Typography>
          </Stack>
        )}

        {preview?.status === 'Aplicada' && (
          <Alert severity="success">{preview.applyNote || 'Listo.'}</Alert>
        )}

        {preview?.status === 'Lista' && (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {preview.scanned} ventas revisadas: <b>{preview.diffCount}</b> con diferencias y <b>{preview.newCount}</b> que no están en el sistema.
              Marcá Sí o No en cada una; lo que marques No queda como está.
            </Typography>
            {items.length === 0 ? (
              <Alert severity="success">Todo coincide con el canal: no hay nada para cambiar.</Alert>
            ) : (
              <>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Tabs value={tab} onChange={(_, v) => { setTab(v); setPage(1); }} sx={{ flex: 1 }}>
                    <Tab value="diff" label={`Con diferencias (${preview.diffCount})`} />
                    <Tab value="new" label={`Nuevas (${preview.newCount})`} />
                  </Tabs>
                  <Button size="small" onClick={() => setAll(true)} disabled={list.length === 0}>Sí a todas</Button>
                  <Button size="small" onClick={() => setAll(false)} disabled={list.length === 0}>No a todas</Button>
                </Stack>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Venta</TableCell>
                      <TableCell>Fecha de la venta</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell align="right">Total</TableCell>
                      {tab === 'diff' && <TableCell>Qué cambia (actual → canal)</TableCell>}
                      <TableCell align="center">¿Aplicar?</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageItems.map(i => (
                      <TableRow key={i.externalId} hover>
                        <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{i.reference || i.externalId}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtWhen(i.saleDate)}</TableCell>
                        <TableCell>{i.customer || '—'}</TableCell>
                        <TableCell align="right">{fmtMoney(i.total)}</TableCell>
                        {tab === 'diff' && (
                          <TableCell>
                            {i.diffs.map(d => (
                              <Typography key={d.field} variant="body2">
                                <b>{d.label}:</b> {fmtValue(d, d.current)} → <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>{fmtValue(d, d.incoming)}</Box>
                              </Typography>
                            ))}
                          </TableCell>
                        )}
                        <TableCell align="center">
                          <ToggleButtonGroup size="small" exclusive value={decisions[i.externalId] === undefined ? null : decisions[i.externalId] ? 'si' : 'no'}
                            onChange={(_, v) => v && setDecisions(d => ({ ...d, [i.externalId]: v === 'si' }))}>
                            <ToggleButton value="si" color="success">Sí</ToggleButton>
                            <ToggleButton value="no" color="error">No</ToggleButton>
                          </ToggleButtonGroup>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {list.length > PAGE && (
                  <Stack alignItems="center" sx={{ mt: 1 }}>
                    <Pagination count={Math.ceil(list.length / PAGE)} page={page} onChange={(_, p) => setPage(p)} size="small" />
                  </Stack>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {preview?.status === 'Lista' && items.length > 0 && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, pl: 1 }}>
            <Chip size="small" label={`Decididas ${decided} de ${items.length}`} color={decided === items.length ? 'success' : 'default'} />
            {decided < items.length && <Typography variant="caption" color="text.secondary">Falta decidir {items.length - decided}</Typography>}
          </Stack>
        )}
        <Button onClick={onClose}>{preview?.status === 'Aplicada' ? 'Cerrar' : 'Cancelar'}</Button>
        {preview?.status === 'Lista' && items.length > 0 && (
          <Button variant="contained" onClick={apply} disabled={applying || decided < items.length}>
            Aplicar {accepted.length} aprobados
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ResyncDialog;
